import { logCareerScoutProductButtonTap, logCareerScoutProductImpression } from '../../../../../ace/logging/signals';
import { AILogo, Box, Pressable, Text } from '../../../../../mads';
import { useI18n } from '../../../../../shared/I18n';
import {
    encodeViewjobSourceContext,
    getConversationForViewjob,
    setActiveViewjobJobKey,
} from '../../../../../shared/lib/conversationStarter/ConversationStarterStorage';
import { JSTModalScreen } from '../../../../../shared/native-modules/tare/JSTModalScreen';
import { openModal } from '../../../../../shared/native-modules/tare/openModal';
import { useRunOnceAfterMount } from '../../../../../shared/util/useRunOnceAfterMount';
import { useViewjobOneGraphModel } from '../../../context/ViewJobDataContext';

import { colors, spacing } from '@indeed/ifl-tokens/react-native';
import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';

interface AskCareerScoutCTAProps {
    compact?: boolean;
}

export const AskCareerScoutCTA: React.FC<AskCareerScoutCTAProps> = ({ compact = false }) => {
    const gt = useI18n();
    const ogModel = useViewjobOneGraphModel();
    const jobKey = ogModel?.job.jobKey;
    const jobTitle = ogModel?.job.jobTitle;
    const companyName = ogModel?.employer?.companyName;

    useRunOnceAfterMount(() => {
        logCareerScoutProductImpression({
            elementName: 'askCareerScout',
            assistantMessageId: null,
            from: 'viewJob',
            jobKey: jobKey ?? undefined,
        });
    });

    const handlePress = useCallback(() => {
        const existingConversationId = jobKey ? getConversationForViewjob(jobKey) : null;
        if (jobKey) {
            setActiveViewjobJobKey(jobKey);
        }

        logCareerScoutProductButtonTap({
            elementName: 'askCareerScout',
            assistantMessageId: null,
            from: 'viewJob',
            jobKey: jobKey ?? undefined,
        });

        void openModal(
            JSTModalScreen.aceConversation({
                conversationId: existingConversationId,
                sourceContext: jobKey
                    ? encodeViewjobSourceContext(jobKey, { jobTitle, companyName })
                    : 'viewjob',
            }),
            'viewjob',
        );
    }, [jobKey, jobTitle, companyName]);

    const label = compact ? gt.gettext('Ask') : gt.gettext('Ask Career Scout');

    return (
        <Pressable
            onPress={handlePress}
            sx={[styles.button, compact && styles.buttonCompact]}
            accessibilityRole="button"
            accessibilityLabel={gt.gettext('Ask Career Scout')}
        >
            <Box sx={styles.content}>
                <AILogo size={compact ? 'sm' : 'md'} />
                <Text
                    level={compact ? 2 : 3}
                    sx={styles.label}
                >
                    {label}
                </Text>
            </Box>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    button: {
        borderWidth: 1,
        borderColor: colors.borderWeak,
        borderRadius: 8,
        paddingVertical: spacing.s3,
        paddingHorizontal: spacing.s4,
        backgroundColor: colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonCompact: {
        minHeight: 44,
        paddingVertical: spacing.s2,
        paddingHorizontal: spacing.s4,
        borderRadius: 12,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.s2,
    },
    label: {
        color: colors.foregroundActionPrimary,
        fontWeight: '700',
    },
});
